import React, { useState, useEffect, useCallback } from 'react';
import { X, Save, Beaker } from 'lucide-react';
import { dataMappingAPI } from '../../services/api';
import toast from 'react-hot-toast';

interface AddRegimenModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AddRegimenModal: React.FC<AddRegimenModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    regimen_id: '',
    generic_name: '',
    abbreviation1: '',
    abbreviation2: '',
    internal_name1: '',
    internal_name2: '',
    internal_name3: '',
    internal_name4: '',
    internal_name5: '',
    internal_name6: '',
    internal_name7: '',
    internal_name8: '',
    internal_name9: '',
    internal_name10: '',
    med_ids: [] as string[],
  });
  const [availableMedications, setAvailableMedications] = useState<any[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [draftSaved, setDraftSaved] = useState(false);
  const draftKey = 'regimen_mapping_draft_v1';

  // Load available medications
  useEffect(() => {
    if (isOpen) {
      const loadMedications = async () => {
        try {
          const medications = await dataMappingAPI.getMedications();
          setAvailableMedications(medications);
        } catch (error) {
          console.error('Error loading medications:', error);
          toast.error('Failed to load medications');
        }
      };
      loadMedications();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      // Restore draft
      try {
        const saved = localStorage.getItem(draftKey);
        if (saved) {
          const parsed = JSON.parse(saved);
          setFormData(prev => ({
            ...prev,
            ...parsed,
          }));
        }
      } catch (error) {
        console.error('Error loading draft:', error);
      }
    }
  }, [isOpen]);

  // Function to save draft
  const saveDraft = useCallback(() => {
    try {
      // Only save if there's actual content
      const hasContent = formData.regimen_id.trim() || 
                        formData.generic_name.trim() || 
                        formData.abbreviation1.trim() ||
                        formData.abbreviation2.trim() ||
                        formData.med_ids.length > 0;
      
      if (hasContent) {
        localStorage.setItem(draftKey, JSON.stringify(formData));
        setDraftSaved(true);
        // Hide the "draft saved" indicator after 2 seconds
        setTimeout(() => setDraftSaved(false), 2000);
      }
    } catch (error) {
      console.error('Error saving draft:', error);
    }
  }, [formData, draftKey]);

  // Autosave draft when formData changes (with debouncing)
  useEffect(() => {
    if (!isOpen) return;
    const timeoutId = setTimeout(saveDraft, 500); // Debounce for 500ms
    return () => clearTimeout(timeoutId);
  }, [formData, isOpen, saveDraft]);

  // Save draft when window loses focus
  useEffect(() => {
    const handleBeforeUnload = () => {
      saveDraft();
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        saveDraft();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [saveDraft]);

  const handleInputChange = (field: string, value: string | string[]) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleMedicationToggle = (medId: string) => {
    setFormData(prev => {
      const currentIds = prev.med_ids;
      const newIds = currentIds.includes(medId)
        ? currentIds.filter(id => id !== medId)
        : [...currentIds, medId];
      return { ...prev, med_ids: newIds };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});

    // Validate regimen_id format if provided
    if (formData.regimen_id.trim() && !formData.regimen_id.match(/^R[0-9]{4}$/)) {
      setErrors({ regimen_id: 'Regimen_ID must be in format R#### (e.g., R0001)' });
      setIsSubmitting(false);
      return;
    }

    try {
      await dataMappingAPI.createRegimenMapping(formData);
      toast.success('Regimen mapping created successfully');
      
      // Clear form and draft
      setFormData({
        regimen_id: '',
        generic_name: '',
        abbreviation1: '',
        abbreviation2: '',
        internal_name1: '',
        internal_name2: '',
        internal_name3: '',
        internal_name4: '',
        internal_name5: '',
        internal_name6: '',
        internal_name7: '',
        internal_name8: '',
        internal_name9: '',
        internal_name10: '',
        med_ids: [],
      });
      try { localStorage.removeItem(draftKey); } catch {}
      
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error creating regimen mapping:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to create regimen mapping';
      toast.error(errorMessage);
      
      // Set field-specific errors if available
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <Beaker className="h-6 w-6 text-[var(--accent)]" />
            <h2 className="text-xl font-semibold text-gray-900">Add Regimen Mapping</h2>
            {draftSaved && (
              <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
                Draft saved
              </span>
            )}
          </div>
          <button
            onClick={() => {
              onClose();
              // Clear draft when explicitly closing
              try { localStorage.removeItem(draftKey); } catch {}
            }}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Regimen_ID */}
          <div>
            <label htmlFor="regimen_id" className="block text-sm font-medium text-gray-700 mb-2">
              <Beaker className="inline h-4 w-4 mr-1" />
              Regimen_ID <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="regimen_id"
              value={formData.regimen_id}
              onChange={(e) => handleInputChange('regimen_id', e.target.value.toUpperCase())}
              placeholder="R0001"
              pattern="R[0-9]{4}"
              maxLength={5}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
            />
            <p className="mt-1 text-xs text-gray-500">
              Unique identifier (R + 4 digits, e.g., R0001, R0002). Leave empty to auto-generate.
            </p>
            {errors.regimen_id && (
              <p className="mt-1 text-xs text-red-500">{errors.regimen_id}</p>
            )}
          </div>

          {/* Generic Name */}
          <div>
            <label htmlFor="generic_name" className="block text-sm font-medium text-gray-700 mb-2">
              Generic Name
            </label>
            <input
              type="text"
              id="generic_name"
              value={formData.generic_name}
              onChange={(e) => handleInputChange('generic_name', e.target.value)}
              placeholder="Common generic regimen name"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
            />
            <p className="mt-1 text-xs text-gray-500">
              Commonly used non-proprietary name for combination.
            </p>
          </div>

          {/* Abbreviations */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="abbreviation1" className="block text-sm font-medium text-gray-700 mb-2">
                Abbreviation 1
              </label>
              <input
                type="text"
                id="abbreviation1"
                value={formData.abbreviation1}
                onChange={(e) => handleInputChange('abbreviation1', e.target.value.toUpperCase())}
                placeholder="HRZE, BPaL"
                maxLength={50}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
              />
              <p className="mt-1 text-xs text-gray-500">
                Full regimen name abbreviation (e.g., HRZE, BPaL).
              </p>
            </div>
            <div>
              <label htmlFor="abbreviation2" className="block text-sm font-medium text-gray-700 mb-2">
                Abbreviation 2
              </label>
              <input
                type="text"
                id="abbreviation2"
                value={formData.abbreviation2}
                onChange={(e) => handleInputChange('abbreviation2', e.target.value.toUpperCase())}
                placeholder="Alternative abbreviation"
                maxLength={50}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
              />
              <p className="mt-1 text-xs text-gray-500">
                Regimen abbreviation used in treatment protocols.
              </p>
            </div>
          </div>

          {/* Medications (Multi-select) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Medications <span className="text-red-500">*</span>
            </label>
            <div className="border border-gray-300 rounded-lg p-3 max-h-48 overflow-y-auto">
              {availableMedications.length === 0 ? (
                <p className="text-sm text-gray-500">Loading medications...</p>
              ) : (
                <div className="space-y-2">
                  {availableMedications.map((med: any) => {
                    const medId = med.Med_ID || med.med_id || (med.id ? `M${String(med.id).padStart(4, '0')}` : '');
                    const medName = med.Generic_Name || med.generic_name || med.name || medId;
                    const isSelected = formData.med_ids.includes(medId);
                    
                    return (
                      <label
                        key={medId}
                        className="flex items-center space-x-2 p-2 rounded hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleMedicationToggle(medId)}
                          className="rounded border-gray-300 text-[var(--accent)] focus:ring-[var(--accent)]"
                        />
                        <span className="text-sm text-gray-700">
                          <span className="font-mono font-medium">{medId}</span>
                          {medName !== medId && <span className="ml-2 text-gray-600">- {medName}</span>}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Select one or more medications that are part of this regimen. Must match existing Medication Med_ID.
            </p>
            {formData.med_ids.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {formData.med_ids.map(medId => (
                  <span
                    key={medId}
                    className="inline-flex items-center px-2 py-1 rounded-md bg-blue-50 text-blue-700 text-xs font-medium"
                  >
                    {medId}
                    <button
                      type="button"
                      onClick={() => handleMedicationToggle(medId)}
                      className="ml-1 text-blue-500 hover:text-blue-700"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Internal Names (collapsible section) */}
          <details className="border border-gray-200 rounded-lg p-4">
            <summary className="cursor-pointer text-sm font-medium text-gray-700 mb-4">
              Internal Names (Optional)
            </summary>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                <div key={num}>
                  <label htmlFor={`internal_name${num}`} className="block text-sm font-medium text-gray-700 mb-2">
                    Internal Name {num}
                  </label>
                  <input
                    type="text"
                    id={`internal_name${num}`}
                    value={formData[`internal_name${num}` as keyof typeof formData] as string}
                    onChange={(e) => handleInputChange(`internal_name${num}`, e.target.value)}
                    placeholder={`Internal name ${num}`}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
                  />
                </div>
              ))}
            </div>
          </details>

          {/* Submit buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => {
                onClose();
                // Clear draft when explicitly closing
                try { localStorage.removeItem(draftKey); } catch {}
              }}
              className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || formData.med_ids.length === 0}
              className="px-4 py-2 rounded-lg bg-[var(--accent)] text-white text-sm font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Saving...' : 'Save Regimen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddRegimenModal;
